import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Briefcase,
  CheckCircle2,
  ArrowLeft,
  Loader2,
  Sparkles,
  FileText,
  Clock,
  Zap,
  Shield,
  CreditCard
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { trackBeginCheckout } from "../utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [subscribing, setSubscribing] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("mensuel");

  // Plans Veille Hebdo selon la période
  const veillePlans = {
    mensuel: {
      id: "veille_mensuel",
      price: 90,
      period: "1 mois",
      priceDisplay: "90€",
      periodDisplay: "/mois",
      savings: null
    },
    trimestriel: {
      id: "veille_trimestriel",
      price: 270,
      period: "3 mois",
      priceDisplay: "270€",
      periodDisplay: "/3 mois",
      priceMonthly: "90€/mois",
      savings: null
    },
    semestriel: {
      id: "veille_semestriel",
      price: 432,
      period: "6 mois",
      priceDisplay: "432€",
      periodDisplay: "/6 mois",
      priceMonthly: "72€/mois",
      savings: "Économisez 108€"
    },
    annuel: {
      id: "veille_annuel",
      price: 756,
      period: "1 an",
      priceDisplay: "756€",
      periodDisplay: "/an",
      priceMonthly: "63€/mois",
      savings: "Économisez 324€"
    }
  };

  const currentVeillePlan = veillePlans[selectedPeriod];

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.info("Créez un compte pour souscrire à un abonnement");
      navigate("/register");
      return;
    }

    setSubscribing(planId);
    try {
      // Resolve price for analytics (best-effort, never blocks checkout)
      const priceMap = {
        veille_mensuel: 90,
        veille_trimestriel: 270,
        veille_semestriel: 432,
        veille_annuel: 756,
        pack_memoires: 149,
      };
      trackBeginCheckout({ planId, value: priceMap[planId] || 0 });

      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/api/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("URL de paiement non reçue");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
      setSubscribing(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-slate-900">Mon DCE Express</span>
            </Link>
            
            {user && (
              <Button 
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au dashboard
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
              Nos Packs & Abonnements
            </h1>
            <p className="text-slate-300 text-lg">
              Pour booster vos réponses aux appels d'offres
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          
          {/* Section Veille Hebdo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <Badge className="bg-violet-100 text-violet-700 mb-4">
                <Zap className="w-3 h-3 mr-1" />
                Veille Personnalisée
              </Badge>
              <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">
                Veille Hebdo
              </h2>
              <p className="text-slate-600">
                Recevez chaque semaine les marchés publics adaptés à votre profil
              </p>
            </div>

            {/* Period Selector */}
            <div className="flex justify-center mb-8">
              <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod} className="w-full max-w-lg">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="mensuel" className="text-xs sm:text-sm">1 mois</TabsTrigger>
                  <TabsTrigger value="trimestriel" className="text-xs sm:text-sm">3 mois</TabsTrigger>
                  <TabsTrigger value="semestriel" className="text-xs sm:text-sm">6 mois</TabsTrigger>
                  <TabsTrigger value="annuel" className="text-xs sm:text-sm">1 an</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Veille Card */}
            <Card className="max-w-xl mx-auto bg-white border-2 border-violet-200 shadow-lg">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  {currentVeillePlan.savings && (
                    <Badge className="bg-green-100 text-green-700 mb-4">
                      {currentVeillePlan.savings}
                    </Badge>
                  )}
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-display font-bold text-slate-900">
                      {currentVeillePlan.priceDisplay}
                    </span>
                    <span className="text-slate-500">{currentVeillePlan.periodDisplay}</span>
                  </div>
                  {currentVeillePlan.priceMonthly && (
                    <p className="text-sm text-slate-500 mt-1">
                      soit {currentVeillePlan.priceMonthly}
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {[
                    "Veille hebdomadaire personnalisée",
                    "Recommandations IA illimitées",
                    "Alertes email sur nouveaux marchés",
                    "Accès à toutes les sources (BOAMP, DECP, TED, SNCF...)",
                    "Sauvegarde illimitée de favoris",
                    "Sans engagement - Résiliation en 1 clic"
                  ].map((feature, i) => (
                    <li key={`item-${i}`} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-full py-6 text-lg"
                  onClick={() => handleSubscribe(currentVeillePlan.id)}
                  disabled={subscribing === currentVeillePlan.id}
                  data-testid={`subscribe-${currentVeillePlan.id}`}
                >
                  {subscribing === currentVeillePlan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Payer {currentVeillePlan.priceDisplay}
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-slate-500 mt-4 flex items-center justify-center gap-2">
                  <Shield className="w-4 h-4" />
                  Paiement sécurisé par Stripe
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Section Pack Mémoires */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="text-center mb-8">
              <Badge className="bg-indigo-100 text-indigo-700 mb-4">
                <FileText className="w-3 h-3 mr-1" />
                Aide à la Rédaction
              </Badge>
              <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">
                Pack Mémoires Techniques
              </h2>
              <p className="text-slate-600">
                3 mémoires techniques pré-rédigés par notre IA
              </p>
            </div>

            <Card className="max-w-xl mx-auto bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-200">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <Badge className="bg-indigo-600 text-white mb-4">
                    Paiement unique
                  </Badge>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-display font-bold text-slate-900">149€</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    pour 3 mémoires techniques
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {[
                    "3 mémoires techniques pré-rédigés",
                    "Conçus par un agent IA dédié",
                    "Adaptables à votre entreprise et secteur",
                    "Prêts à déposer avec vos offres",
                    "Support prioritaire inclus"
                  ].map((feature, i) => (
                    <li key={`item-${i}`} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6 text-lg"
                  onClick={() => handleSubscribe("pack_memoires")}
                  disabled={subscribing === "pack_memoires"}
                  data-testid="subscribe-pack-memoires"
                >
                  {subscribing === "pack_memoires" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Payer 149€
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-slate-500 mt-4 flex items-center justify-center gap-2">
                  <Shield className="w-4 h-4" />
                  Paiement sécurisé par Stripe
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Essai Gratuit */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 text-center"
          >
            <Card className="max-w-xl mx-auto bg-white border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-900">Pas encore convaincu ?</h3>
                    <p className="text-sm text-slate-600">Essayez gratuitement pendant 15 jours</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate(user ? "/dashboard" : "/register")}
                    className="ml-auto"
                    data-testid="try-free-pricing"
                  >
                    Essai gratuit
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-12 flex flex-wrap justify-center gap-8 text-slate-400"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm">Paiement sécurisé</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-sm">Sans engagement</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              <span className="text-sm">Activation immédiate</span>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-slate-400">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm">
          <p>© 2025 Mon DCE Express. Tous droits réservés.</p>
          <p className="mt-2">
            Questions ? <a href="mailto:contact@mondceexpress.fr" className="text-white underline">Contactez-nous</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
